import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";
import { IPayment, IReduxState } from "../reducers.interfaces";
import {
    findDeclinedMethodType,
    forwardToThankYouPage,
    isOrderCompleteOrForwardable,
} from "./paymentStates";

export type IPaymentStatesRefreshResult =
    | { status: "complete" }
    | { status: "declined"; methodType: IPayment["method_type"] }
    | { status: "pending" };

/**
 * Read-only re-check of the order's payment states. Safe to call
 * repeatedly: it never invokes payment method handlers, so it cannot
 * re-submit card data. Declines are part of the result because
 * unwrap() strips custom Error properties like `response`.
 */
export const refreshPaymentStates = createAsyncThunk(
    "checkout/refreshPaymentStates",
    async (
        _: void,
        { getState, dispatch },
    ): Promise<IPaymentStatesRefreshResult> => {
        const state = getState() as { checkout: IReduxState };
        const states = await checkoutAPI.checkPaymentStates();

        // Import actions dynamically to avoid circular dependency
        const { updatePaymentStates } = await import("../reducers");
        // Write the states to redux on every outcome,
        // we were losing the GA purchase events (#35199)
        dispatch(updatePaymentStates(states));

        if (isOrderCompleteOrForwardable(states)) {
            forwardToThankYouPage();
            return { status: "complete" };
        }

        const declinedMethodType = findDeclinedMethodType(
            states,
            state.checkout.form.payment_methods,
        );
        if (declinedMethodType) {
            return { status: "declined", methodType: declinedMethodType };
        }
        return { status: "pending" };
    },
);
